import React, { useEffect, useMemo, useState } from 'react';
import { hasFirstHand } from '../utils/dataBasis';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon, ArrowsRightLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import API from '../services/api';
import { formatCaseDate } from '../utils/formatDateValue';
import { getViolationColor, getViolationTypeLabel } from '../types/caseForm';
import { enumLabel } from '../utils/enumLabel';
import { headlineViolation } from '../utils/headlineViolation';
import type { CompareMatch } from './DuplicateCompareDrawer';

/**
 * "Mark as duplicate of…" / "Merge another case into this one". A small search drawer: the
 * researcher finds the other case, sees which of the two is the original (a rule picks the
 * default, a swap button overrides it), and Compare creates the MANUAL match and hands it to the
 * compare drawer. Nothing is written onto either case here, and no model runs.
 *
 * Until a search is typed, the five live cases the engine ranks nearest (the same list as the case
 * page's "Most similar cases" card, GET /api/cases/:id/similar) are offered as suggestions; picking
 * one works exactly like picking a search result.
 *
 * A pair that was dismissed before (including one left by an un-merge) comes back as a warning
 * with the earlier decision and a Merge anyway button; the second call acknowledges it.
 */

export interface PickerCase {
  id: string;
  caseNumber: string;
  status: string;
  isPrimaryData?: boolean;
  dataBasis?: string;
  incidentDate?: string | null;
  country?: string;
  region?: string | null;
  city?: string | null;
  specificLocation?: string | null;
  /** Already-joined place text, as the similar-cases endpoint returns it. */
  place?: string | null;
  violationTypes?: string[];
  otherViolationTypes?: string[];
  createdAt?: string;
}

/** One row of GET /api/cases/:id/similar (the shape SimilarCasesList reads). */
interface SimilarItem {
  id: string; caseNumber: string; status: string; isPrimaryData: boolean; dataBasis?: string; incidentDate: string | null;
  country: string; place: string | null; violationTypes: string[]; otherViolationTypes?: string[]; createdAt?: string;
}

interface Props {
  current: PickerCase;
  /** 'duplicate': the current case is the one being marked as a duplicate of a case to pick.
   *  'canonical': the current case is the one another case will be merged into. */
  mode: 'duplicate' | 'canonical';
  onClose: () => void;
  onCreated: (match: CompareMatch, note?: string) => void;
}

const STATUS_RANK: Record<string, number> = { DRAFT: 0, FOR_VALIDATION: 1, IN_RESEARCH: 2, FOR_APPROVAL: 3, APPROVED: 4 };

/** The default canonical: primary data first, then the case further along the workflow, then the
 *  earlier-recorded one. The researcher can swap. */
export const defaultCanonical = (x: PickerCase, y: PickerCase): PickerCase => {
  if (hasFirstHand(x) !== hasFirstHand(y)) return hasFirstHand(x) ? x : y;
  const rx = STATUS_RANK[x.status] ?? 0; const ry = STATUS_RANK[y.status] ?? 0;
  if (rx !== ry) return rx > ry ? x : y;
  const cx = x.createdAt ? new Date(x.createdAt).getTime() : Infinity;
  const cy = y.createdAt ? new Date(y.createdAt).getTime() : Infinity;
  return cx <= cy ? x : y;
};

const placeOf = (c: PickerCase) => c.place || [c.specificLocation || c.city, c.region].filter(Boolean).join(', ') || c.country || '';

export function CaseRow({ c, selected, onClick }: { c: PickerCase; selected: boolean; onClick: () => void }) {
  const { type: headline, more } = headlineViolation(c.violationTypes, c.otherViolationTypes);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-md border text-sm flex flex-wrap items-center gap-x-3 gap-y-1 ${selected ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
    >
      <span className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{c.caseNumber}</span>
      {hasFirstHand(c) && <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-1 ring-gray-300 dark:ring-gray-600" title="Primary data">P</span>}
      <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap">{c.incidentDate ? formatCaseDate(c.incidentDate) : 'undated'}</span>
      <span className="text-gray-600 dark:text-gray-300 truncate max-w-[14rem]" title={placeOf(c)}>{placeOf(c)}</span>
      {headline && <span className={`px-1.5 py-0.5 text-[11px] rounded-full border ${getViolationColor(headline).soft}`} title={more ? `${more} more violation type${more === 1 ? '' : 's'}` : undefined}>{getViolationTypeLabel(headline)}{more ? ` +${more}` : ''}</span>}
      <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{enumLabel(c.status)}</span>
    </button>
  );
}

export default function MarkDuplicateDrawer({ current, mode, onClose, onCreated }: Props) {
  const [shown, setShown] = useState(false);
  useEffect(() => { const t = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(t); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [q, setQ] = useState('');
  const [results, setResults] = useState<PickerCase[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<PickerCase | null>(null);
  const [swapped, setSwapped] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissal, setDismissal] = useState<string | null>(null); // the 409 message, awaiting "Merge anyway"
  const pick = (c: PickerCase) => { setSelected(c); setSwapped(false); setDismissal(null); setError(null); };

  // Suggestions: what the engine ranks nearest to this case. Listed, never decided; the order is the
  // information. Cases already paired with this one by a dedup match are left out server-side.
  const similar = useQuery<{ items: SimilarItem[]; reason?: string }>({
    queryKey: ['similar-cases', current.id, 'picker'],
    queryFn: async () => (await API.get(`/api/cases/${current.id}/similar`)).data,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const suggestions = useMemo<PickerCase[]>(() => (similar.data?.items ?? []).map((s) => ({
    id: s.id, caseNumber: s.caseNumber, status: s.status, isPrimaryData: s.isPrimaryData, dataBasis: s.dataBasis, incidentDate: s.incidentDate,
    country: s.country, place: s.place, violationTypes: s.violationTypes, otherViolationTypes: s.otherViolationTypes, createdAt: s.createdAt,
  })), [similar.data]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await API.get('/api/cases', { params: { search: term, limit: 8, excludeStatus: 'ARCHIVED,REJECTED' } });
        const list: PickerCase[] = res.data?.cases ?? (Array.isArray(res.data) ? res.data : []);
        if (!cancelled) setResults(list.filter((c) => c.id !== current.id));
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, current.id]);

  // Who ends up canonical: the rule's pick, flipped if the researcher swapped.
  const pair = useMemo(() => {
    if (!selected) return null;
    const ruleCanon = defaultCanonical(current, selected);
    const canonical = swapped ? (ruleCanon.id === current.id ? selected : current) : ruleCanon;
    const duplicate = canonical.id === current.id ? selected : current;
    return { canonical, duplicate, byRule: !swapped };
  }, [current, selected, swapped]);

  const primaryIntoSecondary = !!pair && hasFirstHand(pair.duplicate) && !hasFirstHand(pair.canonical);

  const submit = async (acknowledgeDismissal = false) => {
    if (!pair) return;
    setSubmitting(true); setError(null);
    try {
      const res = await API.post('/api/dedup/matches/manual', {
        caseId: pair.duplicate.id, matchedCaseId: pair.canonical.id, ...(acknowledgeDismissal ? { acknowledgeDismissal: true } : {}),
      });
      const m = res.data?.match;
      const match: CompareMatch = {
        id: m.id, similarityScore: m.similarityScore ?? 0, llmConfidence: m.llmConfidence ?? 0, llmReasoning: m.llmReasoning, source: m.source,
        case: { id: m.case.id, caseNumber: m.case.caseNumber },
        matchedCase: m.matchedCase ? { id: m.matchedCase.id, caseNumber: m.matchedCase.caseNumber } : null,
      };
      onCreated(match, res.data?.existing ? `A ${String(m.status).toLowerCase().replace('_', ' ')} match for this pair already exists — opening it.` : undefined);
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      if (status === 409 && data.error === 'previously_dismissed') {
        setDismissal(data.message || 'This pair was marked "not a duplicate" before.');
      } else {
        setError(data.error || data.message || 'Could not create the match');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'duplicate' ? `Mark ${current.caseNumber} as a duplicate of…` : `Merge another case into ${current.caseNumber}`;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={title}>
      <div className={`fixed inset-0 bg-gray-500/60 transition-opacity duration-300 ${shown ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
      <div className={`fixed inset-y-0 right-0 w-full max-w-2xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${shown ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-start justify-between gap-4 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">Two write-ups of one event. Nothing changes until you confirm in the compare view.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 shrink-0"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Pick a suggestion below, or search by case number, place, Indigenous group or victim name</span>
            <span className="mt-1 relative block">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" aria-hidden="true" />
              <input
                autoFocus
                value={q}
                onChange={(e) => { setQ(e.target.value); setSelected(null); setSwapped(false); setDismissal(null); setError(null); }}
                placeholder="e.g. IPRI-2026-1036, Siang, Mili"
                className="w-full pl-8 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:border-primary focus:ring-primary"
              />
            </span>
          </label>

          <div className="space-y-1">
            {q.trim().length < 2 && (
              <div className="pb-2">
                <div className="px-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Most similar cases</span>
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">Ranked by content similarity. They may or may not be the same event.</span>
                </div>
                {similar.isLoading ? (
                  <div className="mt-1 text-xs text-gray-400 dark:text-gray-500 px-1">Finding similar cases…</div>
                ) : suggestions.length === 0 ? (
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 px-1">
                    {similar.isError ? 'Suggestions are not available right now. Search instead.'
                      : similar.data?.reason === 'no_embedding' ? 'No suggestions yet: this case is indexed for similarity when the duplicate check runs on it. Search instead.'
                      : 'No comparable live cases in the same country. Search instead.'}
                  </div>
                ) : (
                  <div className="mt-1 space-y-1">
                    {suggestions.map((c) => <CaseRow key={c.id} c={c} selected={selected?.id === c.id} onClick={() => pick(c)} />)}
                  </div>
                )}
              </div>
            )}
            {searching && <div className="text-xs text-gray-400 dark:text-gray-500 px-1">Searching…</div>}
            {!searching && q.trim().length >= 2 && results.length === 0 && <div className="text-sm text-gray-500 dark:text-gray-400 px-1">No live cases match. Archived and rejected cases are not listed.</div>}
            {results.map((c) => <CaseRow key={c.id} c={c} selected={selected?.id === c.id} onClick={() => pick(c)} />)}
          </div>

          {pair && (primaryIntoSecondary || dismissal || error) && (
            <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 text-sm">
              {primaryIntoSecondary && (
                <p className="text-xs text-red-700 dark:text-red-300">Primary data cannot be merged into a secondary record. Swap them, or leave both as they are.</p>
              )}
              {dismissal && (
                <div className="text-xs text-amber-800 dark:text-amber-300">
                  <p>{dismissal}</p>
                  <p className="mt-1">A merge raised by hand overrides that decision; the engine will still never suggest this pair on its own.</p>
                </div>
              )}
              {error && <p className="text-xs text-red-700 dark:text-red-300">{error}</p>}
            </div>
          )}
        </div>

        {/* Footer: what will happen, beside the button that does it. */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0 text-sm text-gray-700 dark:text-gray-200">
            {pair ? (
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>
                  Original: <span className="font-semibold text-gray-900 dark:text-gray-100">{pair.canonical.caseNumber}</span>
                  <span className="text-gray-500 dark:text-gray-400"> ({hasFirstHand(pair.canonical) ? 'first-hand data' : enumLabel(pair.canonical.status).toLowerCase()}{pair.byRule ? ', by rule' : ', your choice'})</span>.
                  {' '}Duplicate: <span className="font-semibold text-gray-900 dark:text-gray-100">{pair.duplicate.caseNumber}</span>, archived into it.
                </span>
                <button type="button" onClick={() => setSwapped((s) => !s)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <ArrowsRightLeftIcon className="h-3.5 w-3.5" /> Swap
                </button>
              </span>
            ) : <span className="text-gray-400 dark:text-gray-500">Pick the other case above.</span>}
          </div>
          <div className="flex items-center justify-end gap-2 shrink-0">
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            {dismissal ? (
              <button type="button" disabled={submitting || !pair || primaryIntoSecondary} onClick={() => submit(true)} className="px-3 py-1.5 rounded-md bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {submitting ? 'Opening…' : 'Merge anyway → Compare'}
              </button>
            ) : (
              <button type="button" disabled={submitting || !pair || primaryIntoSecondary} onClick={() => submit(false)} className="px-3 py-1.5 rounded-md bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {submitting ? 'Opening…' : 'Compare →'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
